/**
 * @author Zdenka Valkučáková <zdenka.valkucakova@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import EventEmitter from 'eventemitter3';

/**
 * @import { IStatusInfoDialogController, StatusInfoDialogControllerEventTypes, StatusInfoDialogOpenStrategy } from '.'
 * @import { Destroyable } from '../destroyable'
 */

/** Události, které resetují prodlevu nečinnosti. */
const activityEvents = ['keydown', 'mousemove'];

/** Prodleva pro zobrazení dialogu po stisknutí tlačítka. */
const inactivityDuration = 30000;

/**
 * Obdoba Observer patternu.
 * @implements {IStatusInfoDialogController}
 */
export default class StatusInfoDialogController {
  /**
   * @param {StatusInfoDialogOpenStrategy[]} strategies
   */
  constructor(...strategies) {
    /** @type {EventEmitter<StatusInfoDialogControllerEventTypes>} */
    this.emitter = new EventEmitter();
    /** @type {ReturnType<typeof setTimeout> | null} */
    this.inactivityTimeout = null;
    /** @type {() => void} */
    this.onActivity = this.onActivity.bind(this);
    activityEvents.forEach((event) => {
      document.addEventListener(event, this.onActivity);
    });
    /** @private */
    this.onOpen = this.open.bind(this);
    /** @private */
    this.onClose = this.close.bind(this);
    this.strategies = strategies;
    this.strategies.forEach((strategy) => strategy.on('open', this.onOpen));
  }

  /** @type {IStatusInfoDialogController['close']} */
  close() {
    this.clearInactivityTimeout();
    this.emitter.emit('close');
  }

  /** @private */
  clearInactivityTimeout() {
    if (this.inactivityTimeout) {
      clearTimeout(this.inactivityTimeout);
      this.inactivityTimeout = null;
    }
  }

  /** @type {Destroyable['destroy']} */
  destroy() {
    this.clearInactivityTimeout();
    this.strategies.forEach((strategy) => strategy.off('open', this.onOpen));
    this.strategies.forEach((strategy) => strategy.destroy());
    activityEvents.forEach((event) =>
      document.removeEventListener(event, this.onActivity)
    );
  }

  /** @type {IStatusInfoDialogController['off']} */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @type {IStatusInfoDialogController['on']} */
  on(...args) {
    this.emitter.on(...args);
  }

  /** @private */
  onActivity() {
    if (!this.inactivityTimeout) {
      return;
    }
    this.resetInactivityTimeout();
    this.emitter.emit('activity', inactivityDuration);
  }

  /** @type {IStatusInfoDialogController['open']} */
  open() {
    const { inactivityTimeout } = this;
    this.resetInactivityTimeout();
    if (inactivityTimeout) {
      this.emitter.emit('activity', inactivityDuration);
      return;
    }
    this.emitter.emit('open', inactivityDuration);
  }

  /** @private */
  resetInactivityTimeout() {
    this.clearInactivityTimeout();
    this.inactivityTimeout = setTimeout(this.onClose, inactivityDuration);
  }
}

export const internal = {
  activityEvents,
  inactivityDuration
};
